/**
 * Enum containing the keys used for invalidating react query
 */
export enum QueryKeys {
  RecruitingMessageContext = 'recruitingMessageContext',
  RecruitingMessageTemplates = 'recruitingMessageTemplates',
  Recruiting = 'recruiting',
  RecruitingNotes = 'recruitingNotes',
  /** A résumé's signed URL; kept apart so unrelated writes never reload it. */
  RecruitingResume = 'recruitingResume',
  /** Nested under `Recruiting`, so every workspace write refreshes them. */
  RecruitingApplication = 'recruitingApplication',
  RecruitingApplicationEvents = 'recruitingApplicationEvents',
  RecruitingTags = 'recruitingTags',
  /** Nested under `Recruiting`, so every workspace write refreshes them. */
  RecruitingTalentPool = 'recruitingTalentPool',
  RecruitingTalentMatches = 'recruitingTalentMatches',
  /** Scorecards (WP8), nested under `Recruiting` like the profile. */
  RecruitingScorecards = 'recruitingScorecards',
  RecruitingScorecardCriteria = 'recruitingScorecardCriteria',
  RecruitingScorecardCounts = 'recruitingScorecardCounts',
  RecruitingInterviewNotes = 'recruitingInterviewNotes',
  RecruitingCalendarStatus = 'recruitingCalendarStatus',
  RecruitingCalendarPreview = 'recruitingCalendarPreview',
  RecruitingInterviewers = 'recruitingInterviewers',
  /** An applicant's shares with colleagues, nested under `Recruiting`. */
  RecruitingShares = 'recruitingShares',
  RecruitingShareRecipients = 'recruitingShareRecipients',
  RecruitingBookedInterview = 'recruitingBookedInterview',
  RecruitingApplicationPage = 'recruitingApplicationPage',
  RecruitingAnalyticsOverview = 'recruitingAnalyticsOverview',
  RecruitingAnalyticsJobs = 'recruitingAnalyticsJobs',
  RecruitingJobillico = 'recruitingJobillico',
  /** Nested under `Recruiting`: the board catalog and a job's board rows. */
  RecruitingBoards = 'recruitingBoards',
  /** Nested under `Recruiting`, so every workspace write refreshes them. */
  RecruitingCandidateEmails = 'recruitingCandidateEmails',
  RecruitingCandidateEmailSettings = 'recruitingCandidateEmailSettings',
  /** The company's locations, for the job form's location picker. */
  RecruitingJobLocations = 'recruitingJobLocations',
  Company = 'company',
  Companies = 'companies',
  CompanyFeatures = 'companyFeatures',
  CompanyReadiness = 'companyReadiness',
  ReminderRules = 'reminderRules',
  ReminderCompliance = 'reminderCompliance',
  ReminderExpiryCoverage = 'reminderExpiryCoverage',
  DocumentTemplates = 'documentTemplates',
  DocumentTemplateVersions = 'documentTemplateVersions',
  DocumentFieldGaps = 'documentFieldGaps',
  DocumentBroadcasts = 'documentBroadcasts',
  BroadcastAudienceCount = 'broadcastAudienceCount',
  SignatureRequests = 'signatureRequests',
  SignatureRequestPeople = 'signatureRequestPeople',
  ApplicationOffer = 'applicationOffer',
  Timezones = 'timezones',
  Profile = 'profile',
  CompanyAdmins = 'companyAdmins',
  LeaveSchedule = 'leaveSchedule',
  LeaveHolidays = 'leaveHolidays',
  HolidaySettings = 'holidaySettings',
  HolidayImportPreview = 'holidayImportPreview',
  PublicHolidayCountries = 'publicHolidayCountries',
  HolidayBranchLists = 'holidayBranchLists',
  EmployeeLeaveBalances = 'employeeLeaveBalances',
  EmployeeImportJobs = 'employeeImportJobs',
  EmployeeImportJobDetails = 'employeeImportDetails',
  EmployeeImportTemplateValues = 'employeeImportTemplateValues',
  EmployeeLeaveRequests = 'EmployeeLeaveRequests',
  CompanyBusinessAddresses = 'companyBusinessAddresses',
  CompanyLeaveTypes = 'companyLeaveTypes',
  CompanyLeavePolicies = 'companyLeavePolicies',
  PolicyLeaveTypes = 'policyLeaveTypes',
  LeaveCyclesStatus = 'leaveCyclesStatus',
  EmployeeLeaveYears = 'employeeLeaveYears',
  LeaveYearSwitches = 'leaveYearSwitches',
  PolicyBasedEmployees = 'policyBasedEmployees',
  FieldSections = 'field-sections',
  FieldSets = 'field-sets',
  FieldSet = 'field-set',
  FieldOptions = 'field-options',
  AssetTypes = 'asset-types',
  AssetType = 'asset-type',
  AssetTypesGetStartedOptions = 'asset-types-get-started-options',
  Lookup = 'lookup',
  AssetModels = 'asset-models',
  AssetModel = 'asset-model',
  AssetUnits = 'asset-units',
  PotentialAllocationSchedules = 'potential-allocation-schedules',
  DirectGrantLeaves = 'direct-grant-leaves',
  ScheduledGrantLeaves = 'scheduled-grant-leaves',
  Countries = 'countries',
  Provinces = 'provinces',
  EligibleLeavePolicies = 'eligible-leave-policies',
  EligibleLeavePoliciesUpdate = 'eligible-leave-policies-after-update',
  SlackStatus = 'slack-status',
  McpClients = 'mcp-clients',
  McpConnectionStatus = 'mcp-connection-status',
  McpCompanySessions = 'mcp-company-sessions',
  TeamsStatus = 'teams-status',
  TimesheetEmployeesWeek = 'timesheet-employees-week',
  TimesheetEmployeeRange = 'timesheet-employee-range',
  TimesheetRequests = 'timesheet-requests',
  TimesheetEmployeeDay = 'timesheet-employee-day',
  TimesheetRequestsCount = 'timesheet-requests-count',
  CompanyShiftPolicy = 'company-shift-policy',
  CompanyNotificationSettings = 'company-notification-settings',
  AdminManagedEmployees = 'adminManagedEmployees',
  Changelog = 'changelog',
  ChatHistory = 'chatHistory',
  ChatThreads = 'chatThreads',
  /** `[UserPreference, userId, key]`: the signed-in user's own preference. */
  UserPreference = 'userPreference',
  /** `[EmployeeExportColumns, companyId, language]`: the export catalogue. */
  EmployeeExportColumns = 'employeeExportColumns',
  /** `[Trainings, companyId, filters]`: the Training list. */
  Trainings = 'trainings',
  /** `[TrainingDetail, companyId, trainingId]`: one training and its versions. */
  TrainingDetail = 'trainingDetail',
  /** `[TrainingChapterMedia, companyId, chapterId]`: signed preview URLs. */
  TrainingChapterMedia = 'trainingChapterMedia',
  /** `[TrainingRules, companyId, trainingId]`: a training's assignment rules. */
  TrainingRules = 'trainingRules',
  /** `[TrainingResults, companyId, trainingId, ...]`: Results rows, KPIs, drawer. */
  TrainingResults = 'trainingResults',
  /** `[EmployeeTrainings, companyId, employeeId]`: profile > Trainings. */
  EmployeeTrainings = 'employeeTrainings',
  /** `[TrainingAnalytics, companyId, params]`: Analytics > Training. */
  TrainingAnalytics = 'trainingAnalytics',
  /** `[TrainingExternal, companyId, filters]`: the External trainings queue. */
  TrainingExternal = 'trainingExternal',
  /** `[TrainingSpend, companyId, year]`: the spend ledger. */
  TrainingSpend = 'trainingSpend',
  /** `[TrainingSpendSettings, companyId]`: hours basis, contributions. */
  TrainingSpendSettings = 'trainingSpendSettings',
  /** `[EmployeeExternalTrainings, companyId, employeeId]`: profile entries. */
  EmployeeExternalTrainings = 'employeeExternalTrainings',
  /** `[PayrollExportPreview, companyId, from, to, branchId, language]`. */
  PayrollExportPreview = 'payrollExportPreview',
  /** `[PayrollSettings, companyId]`: provider, schedule, overtime rule. */
  PayrollSettings = 'payrollSettings',
  /** `[PayrollPeriods, companyId, branchId]`: periods and their status. */
  PayrollPeriods = 'payrollPeriods',
  /** `[PayrollRuns, companyId, branchId]`: the export history. */
  PayrollRuns = 'payrollRuns',
  /** `[PayrollCodes, companyId]`: the Codes tab. */
  PayrollCodes = 'payrollCodes',
  /** `[PayrollEmployees, companyId]`: payroll employee numbers. */
  PayrollEmployees = 'payrollEmployees',
}
